import { distanceMi } from './geo';
import { byStart, endDateOf, isMoveType, startDateOf } from './items';
import type { Item, Place, Trip } from './schema';
import { addDays, diffDays } from './time';

/**
 * Which trip a new plan belongs to, the same way on the server (email imports, Claude) and in
 * the apps (a plan added by hand): a trip whose dates it touches, give or take the gap
 * setting, and while a trip's last flight or train left me away from home, the weeks after it
 * too, since the way home (or the next leg) can still come. A trip with fixed dates widens to
 * take a plan that joins it; a trip without them takes its dates from its plans anyway.
 */

export interface DateRange {
  start: string;
  end: string;
}

export interface Away {
  home: Pick<Place, 'lat' | 'lon' | 'city'> | null;
  radiusMi: number;
  place: (id: string | null) => Place | undefined;
}

/** Days a trip stays open on a side where the traveler is still away from home. */
export const OPEN_DAYS = 21;

export function isAway(p: Place | undefined, away: Away): boolean {
  const h = away.home;
  if (!p || !h) return false;
  if (p.lat != null && p.lon != null && h.lat != null && h.lon != null)
    return distanceMi({ lat: p.lat, lon: p.lon }, { lat: h.lat, lon: h.lon }) > away.radiusMi;
  return !!p.city && !!h.city && p.city !== h.city;
}

/**
 * A trip's dates, widened while it's open: when its last flight or train ends away from home,
 * the way back can still come (and the same for a first leg that starts away). Fixed dates
 * count as the trip's dates, but don't close it.
 */
export function tripRange(
  trip: Trip,
  items: Item[],
  gapDays: number,
  away: Away,
): DateRange | null {
  const mine = items
    .filter((i) => !i.deleted && i.tripId === trip.id && i.status === 'active' && i.startAt != null)
    .sort(byStart);
  const starts = mine
    .map(startDateOf)
    .filter((d): d is string => !!d)
    .sort();
  const ends = mine
    .map(endDateOf)
    .filter((d): d is string => !!d)
    .sort();
  const start = trip.startDate ?? starts[0];
  const end = trip.endDate ?? ends[ends.length - 1];
  if (!start || !end) return null;
  const moves = mine.filter((i) => isMoveType(i.type));
  const first = moves[0];
  const last = moves[moves.length - 1];
  const openBefore = !!first && isAway(away.place(first.startPlaceId), away);
  const openAfter = !!last && isAway(away.place(last.endPlaceId), away);
  return {
    start: addDays(start, -(openBefore ? Math.max(OPEN_DAYS, gapDays) : gapDays)),
    end: addDays(end, openAfter ? Math.max(OPEN_DAYS, gapDays) : gapDays),
  };
}

/** The existing trip whose (widened) dates a plan touches, nearest first, if any. */
export function overlappingTrip(
  plan: DateRange,
  trips: Trip[],
  items: Item[],
  gapDays: number,
  away: Away,
): Trip | undefined {
  let best: { trip: Trip; distance: number } | undefined;
  for (const t of trips) {
    if (t.deleted) continue;
    const r = tripRange(t, items, gapDays, away);
    if (!r) continue;
    if (plan.start <= r.end && plan.end >= r.start) {
      const distance = Math.min(
        Math.abs(diffDays(r.start, plan.start)),
        Math.abs(diffDays(plan.end, r.end)),
      );
      if (!best || distance < best.distance) best = { trip: t, distance };
    }
  }
  return best?.trip;
}

/**
 * A trip's fixed dates stretched to take a plan, or null when there's nothing to change: no
 * fixed dates (the trip follows its plans), or they already cover it. Never shrinks a trip.
 */
export function widenedDates(
  trip: Pick<Trip, 'startDate' | 'endDate'>,
  plan: DateRange,
): Partial<Pick<Trip, 'startDate' | 'endDate'>> | null {
  const fields: Partial<Pick<Trip, 'startDate' | 'endDate'>> = {};
  if (trip.startDate && plan.start < trip.startDate) fields.startDate = plan.start;
  if (trip.endDate && plan.end > trip.endDate) fields.endDate = plan.end;
  return Object.keys(fields).length ? fields : null;
}

/** The local days a plan covers. */
export function planDates(
  item: Pick<Item, 'startAt' | 'startTz' | 'endAt' | 'endTz'>,
): DateRange | null {
  const start = startDateOf(item as Item);
  if (!start) return null;
  return { start, end: endDateOf(item as Item) ?? start };
}
